import { StateCreator } from 'zustand';
import { MetaLayerDefinition, SearchResultFlat } from '@agissc/ktag-map';
import { IAppStore } from './IAppStore';
import UIStateSlice, {
  ToolbarState,
  FlyinNames,
  Notification,
  UIStateSliceProps,
  AccordionName,
} from './UIStateSlice';

export const defaultUIStateProps: UIStateSliceProps = {
  toolbar: { isOpen: false, isToolbarExtensionOpen: false },
  favorites: {},
  basemapSelectionVisibility: true,
  toolDialogWidth: 400,
  activeFlyin: null,
  activeFlyinView: null,
  flyinWidth: 400,
  viewCollapse: false,
  notifications: [],
  disabledNotificationTitle: [],
  navigationIsEnabled: true,
  displayResetNotification: false,
  displayStartUpMessage: false,
  accordions: {
    [AccordionName.MAP_CONFIGURATION]: [],
    [AccordionName.DRAW_CONFIGURATION]: [],
    [AccordionName.ATTRIBUTESEARCH_CONFIGURATION]: [],
    [AccordionName.IDENTIFY_CONFIGURATION]: [],
  },
};

const createUIStateSlice: StateCreator<IAppStore, [], [], UIStateSlice> = (set, get) => ({
  ...defaultUIStateProps,
  setToolbar: (input: ToolbarState) =>
    set((state: UIStateSlice) => ({ ...state, toolbar: input })),
  toggleToolbar: () => {
    const current = get().toolbar;
    set({ toolbar: { ...current, isOpen: !current.isOpen } });
  },
  toggleToolbarExtension: () => {
    const current = get().toolbar;
    set({
      toolbar: {
        ...current,
        isToolbarExtensionOpen: !current.isToolbarExtensionOpen,
      },
    });
  },
  toggleFavorite: (item: SearchResultFlat | MetaLayerDefinition) => {
    const newFavorites = { ...get().favorites };
    const key = item.id as string;

    if (key in newFavorites) {
      delete newFavorites[key];
    } else {
      newFavorites[key] = item;
    }

    set({ favorites: newFavorites });
  },
  toggleBasemapSelectionVisibility: () => {
    set({ basemapSelectionVisibility: !get().basemapSelectionVisibility });
  },
  setToolDialogWidth: (input: number) => set({ toolDialogWidth: input }),
  setActiveFlyin: (input: FlyinNames | null, view: string | null = null) =>
    set({ activeFlyin: input, activeFlyinView: view }),
  setActiveFlyinView: (input: string | null = null) => set({ activeFlyinView: input }),
  setFlyinWidth: (input: number) => set({ flyinWidth: input }),
  toggleViewCollapse: () => set({ viewCollapse: !get().viewCollapse }),
  addNotification: (input: Notification) =>
    set({
      notifications: [
        ...get().notifications.filter((item) => item.id !== input.id),
        input,
      ],
    }),
  removeNotification: (id: string | string[]) => {
    const ids = Array.isArray(id) ? id : [id];
    set({ notifications: get().notifications.filter((item) => !ids.includes(item.id)) });
  },
  disableNotificationTitle: (title: string) => {
    set({ disabledNotificationTitle: [...get().disabledNotificationTitle, title] });
  },
  resetNotifications: () => set({ disabledNotificationTitle: [] }),
  setNavigationIsEnabled: (value: boolean) => set({ navigationIsEnabled: value }),
  unsetResetNotification: () => set({ displayResetNotification: false }),
  toggleStartUpMessage: () =>
    set({ displayStartUpMessage: !get().displayStartUpMessage }),
  setAccordionItem: (accordionName, itemId, expanded) => {
    const { accordions } = get();
    const items = accordions[accordionName] || [];
    const index = items.findIndex((i) => i.id === itemId);

    if (index > -1 && items[index].expanded === expanded) {
      return;
    }

    const updatedItems =
      index > -1
        ? items.map((i) => (i.id === itemId ? { ...i, expanded } : i))
        : [...items, { id: itemId, expanded }];

    set({
      accordions: {
        ...accordions,
        [accordionName]: updatedItems,
      },
    });
  },
  setAllAccordionItems: (accordionName, itemIds, expanded) => {
    const { accordions } = get();
    const items = accordions[accordionName] || [];

    const updatedItems = itemIds.map((id) => {
      const existing = items.find((i) => i.id === id);
      return { id, expanded: existing ? expanded : expanded };
    });

    const allSame =
      items.length === updatedItems.length &&
      items.every((i) =>
        updatedItems.find((u) => u.id === i.id && u.expanded === i.expanded),
      );
    if (allSame) return;

    set({
      accordions: {
        ...accordions,
        [accordionName]: updatedItems,
      },
    });
  },
  removeAllAccordionItems: (accordionName) => {
    const { accordions } = get();
    set({
      accordions: {
        ...accordions,
        [accordionName]: [],
      },
    });
  },
});

export default createUIStateSlice;
